import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const swabTest: ContentBlock<"procedure"> = {
  id: makeId("proc-swab-test"),
  kind: "procedure",
  tags: ["testing", "her", "clinic-visit"],
  title: "Swab test",
  subtitle: "Cervical or vaginal swab to screen for infections",
  body: {
    procedureName: "Swab test",
    category: "diagnostic",
    description:
      "A quick test where a small swab is used to collect cells from the cervix or vagina to screen for infections such as chlamydia, gonorrhea, bacterial vaginosis, or mycoplasma. Treating infections before fertility treatment improves outcomes.",
    howItWorks:
      "You lie on an exam table with your feet in stirrups. A speculum is gently inserted to visualize the cervix. A soft-tipped swab is then used to collect a small sample of cells from the cervix or vaginal walls. The swab is placed in a transport tube and sent to the lab for culture or PCR testing. Some clinics offer self-collection swabs for certain infections, where you collect the sample yourself in a private room.",
    duration: "5–10 minutes",
    anesthesia: "none",
    whatToExpect: [
      "Brief discomfort when the speculum is inserted and the swab is taken",
      "The swab itself takes only a few seconds",
      "Results are usually available within 2–5 business days",
      "If an infection is detected, antibiotics are prescribed before proceeding with treatment",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Most swab results return within 2-5 business days, depending on whether PCR or culture is used.",
      },
      {
        label: "If positive",
        detail:
          "Your care team should explain treatment, partner testing if relevant, and when fertility treatment can continue.",
      },
      {
        label: "If negative",
        detail:
          "A negative result usually clears this infection screen for the current treatment window, subject to clinic policy.",
      },
    ],
    prepInstructions: [
      "Avoid vaginal douching for 24 hours before the test",
      "Avoid intercourse for 24 hours before if your clinic recommends it",
      "Let your clinic know if you are currently menstruating, as some tests are better done at other times",
    ],
    risks: [
      "Very mild discomfort during the swab, not painful",
      "Rare: very light spotting afterward",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Prélèvement vaginal",
      description:
        "Examen rapide consistant à recueillir des cellules au niveau du col ou du vagin pour dépister des infections (chlamydia, gonocoque, vaginose bactérienne, mycoplasme). Traiter une infection avant l'AMP (assistance médicale à la procréation) améliore les résultats.",
      howItWorks:
        "Vous êtes allongée sur la table d'examen, les pieds dans des étriers. Un spéculum est inséré délicatement pour visualiser le col. Un écouvillon à pointe douce recueille un petit échantillon de cellules au niveau du col ou des parois vaginales. L'écouvillon est placé dans un tube de transport et envoyé au laboratoire pour mise en culture ou PCR. Certains centres proposent un auto-prélèvement pour certaines infections, dans une pièce privative.",
      duration: "5 à 10 minutes",
      whatToExpect: [
        "Léger inconfort à l'introduction du spéculum et au prélèvement",
        "Le prélèvement lui-même ne prend que quelques secondes",
        "Résultats généralement disponibles sous 2 à 5 jours ouvrés",
        "En cas d'infection, des antibiotiques sont prescrits avant de poursuivre le traitement",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "La plupart des résultats de prélèvement reviennent sous 2 à 5 jours ouvrés, selon qu'une PCR ou une culture est utilisée.",
        },
        {
          label: "Si positif",
          detail:
            "Votre équipe doit expliquer le traitement, le test du partenaire si nécessaire et quand le parcours peut continuer.",
        },
        {
          label: "Si négatif",
          detail:
            "Un résultat négatif valide généralement ce dépistage pour la fenêtre de traitement en cours, selon la politique du centre.",
        },
      ],
      prepInstructions: [
        "Évitez les douches vaginales dans les 24 heures précédant l'examen",
        "Évitez les rapports dans les 24 heures précédant l'examen si votre centre le recommande",
        "Signalez à votre centre si vous avez vos règles, certaines analyses étant plus fiables en dehors",
      ],
      risks: [
        "Inconfort très léger pendant le prélèvement, non douloureux",
        "Rare : léger spotting après",
      ],
    },
  },
  sources: [
    {
      title: "Sexually Transmitted Infections Treatment Guidelines, 2021",
      authors: "Workowski KA, Bachmann LH, Chan PA, et al.",
      journal: "MMWR Recommendations and Reports",
      year: 2021,
      url: "https://www.cdc.gov/std/treatment-guidelines/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(swabTest);
